import { DEFAULT_TEXT_TRANSCRIPT_CONFIG } from '@src/services/karaoke/karaokeConstants';
import { LogManager } from '@src/utility/Logger';
import {
  containsWideChar,
  endsSentenceIgnoringAbbreviations,
  endsWithAbbreviation,
  joinPhraseValues,
  needsSpaceBetweenCaptionRows,
  needsSpaceBetweenPhrases,
  visualTextWidth,
} from '@src/utility/strings';

import {
  type PunctuationLineBreak,
  resolvePunctuationLineBreak,
} from './captionLineBreakStyle';
import { computeWordSpans } from './karaokeWordTiming';
import { needsSpaceBefore, reachesWebsiteTld } from './punctuation';
import { applySyntheticBreaksToPhrase } from './syntheticCaptionBreaks';
import { DEFAULT_MINIMUM_TOKEN_DURATION } from './transcriptValidations';

import type { KaraokeClip } from '../models/karaoke/KaraokeClip';
import type { PhraseUnit } from './syntheticCaptionBreaks';
import type { TranscriptTextToken, TranscriptToken } from '@src/editor/types';
import type { TranslationPhrase } from '@src/types.ts/translation';

const logger = LogManager.getLogger('karaoke:segmentation');

// Segmentation constants (from KaraokeSegmenter). Measured in RENDERED visual
// width — the row as `KaraokeClip.getKaraokeText` hands it to the renderer,
// inter-word spaces included — so it means the same thing as Creatomate's
// `transcript_maximum_length` and as the translated path's phrase widths.
export const MAX_SEGMENT_CHAR_LENGTH = 36;

/**
 * The "Words Per Line" presets, as budgets on the RENDERED row width. Calibrated
 * on English interview speech (~5.7 characters per word including its space), so
 * each preset delivers about the number of words its label promises.
 */
export const LINE_LENGTH_PRESETS = {
  oneWord: 3,
  twoWords: 14,
  threeWords: 19,
  fiveWords: 35,
} as const;

/**
 * The budgets those presets used to store. They were calibrated against a
 * measurement that counted the row with its spaces STRIPPED and then overshot by
 * a whole word, so read literally as rendered widths they under-deliver by
 * roughly 40% — "5 words per line" produced three. Mapping a stored legacy value
 * to its modern equivalent keeps existing stories and caption templates
 * rendering the line length they always did, with no data migration.
 */
const LEGACY_LINE_LENGTH_BUDGETS: Record<number, number> = {
  3: LINE_LENGTH_PRESETS.oneWord,
  8: LINE_LENGTH_PRESETS.twoWords,
  15: LINE_LENGTH_PRESETS.threeWords,
  20: LINE_LENGTH_PRESETS.fiveWords,
};

/**
 * PLAT-11481 — normalize a persisted `transcript_maximum_length` to the
 * segmenter's own default. Some configs and DatoCMS presets store the
 * literal default (100, `DEFAULT_TEXT_TRANSCRIPT_CONFIG`), which means
 * "default line length", not 100-character lines — the UI has always
 * rendered it as the default option. Single source of truth for every
 * consumer (produce original/translated, adopt, track-manager re-split).
 */
export function resolveConfiguredMaxCharLength(
  raw: number | null | undefined,
): number | undefined {
  if (
    raw == null ||
    raw === DEFAULT_TEXT_TRANSCRIPT_CONFIG.transcript_maximum_length
  ) {
    return undefined;
  }
  return LEGACY_LINE_LENGTH_BUDGETS[raw] ?? raw;
}

/**
 * Whether two persisted `transcript_maximum_length` values mean different line
 * lengths. Compares the RESOLVED budgets: a legacy value and its modern
 * equivalent (20 <-> 35) are one setting spelled two ways, and re-producing on
 * that discards every manual caption break for no visible change.
 */
export function lineLengthChanged(
  previous: number | null | undefined,
  next: number | null | undefined,
): boolean {
  return (
    resolveConfiguredMaxCharLength(previous) !==
    resolveConfiguredMaxCharLength(next)
  );
}

type LineBreakSettings = {
  transcript_maximum_length?: number | null;
  line_break_style?: string | null;
};

/**
 * Whether two configs break caption rows differently: a different line-break
 * style, or — with no Punctuation style on either — a different line length.
 * A Punctuation style overrides the length, so under one the length is moot.
 */
export function captionLineBreakChanged(
  previous: LineBreakSettings | null | undefined,
  next: LineBreakSettings | null | undefined,
): boolean {
  const previousStyle = resolvePunctuationLineBreak(previous?.line_break_style);
  const nextStyle = resolvePunctuationLineBreak(next?.line_break_style);
  if (previousStyle !== nextStyle) return true;
  if (nextStyle) return false;
  return lineLengthChanged(
    previous?.transcript_maximum_length,
    next?.transcript_maximum_length,
  );
}
const PAUSE_DURATION_MS = 0.6;
const NATURAL_BEAT_DURATION_MS = 0.8;
// 3 instead of 2 to account for abbreviations like "U.S." that AssemblyAI
// splits into individual letter tokens ("U", "S"), making "The U.S." 3 text
// tokens even though it's visually 2 words.
const ORPHANED_WORDS_COUNT = 3;

/**
 * Represents a karaoke segment with tokens and timing information
 */
export type KaraokeSegment = {
  tokens: TranscriptToken[];
  startTime: number | null;
  endTime: number | null;
  /**
   * PLAT-11481 — set on the sub-segments of an over-length phrase split at
   * produce time. Sub-segments share one token bucket (the timing
   * carrier), so each clip needs offsets to claim only its slice of the
   * bucket's window — the same shape manual synthetic breaks produce
   * (`rebuildPhraseClips`). Interior boundaries only: the first
   * sub-segment has no start offset, the last no end offset.
   */
  startSecondsOffset?: number;
  endSecondsOffset?: number;
  /**
   * PLAT-11481 — shared by an over-length split's sub-segments. Doubles as
   * the reconcile retime-skip flag (a sub-row's phrase timing is a
   * text-proportional slice, deliberately not re-derivable from tokens)
   * and as the hover-grouping id, exactly like manual synthetic breaks.
   */
  siblingGroupId?: string;
  /**
   * PLAT-9621: when the segment came from phrase-boundary segmentation,
   * the resolved translated string for the line. Producer copies it onto
   * the emitted KaraokeClip so display text uses the translation while
   * the source `tokens` keep their role as the timing carrier.
   */
  translatedText?: string;
  /**
   * PLAT-9621: per-phrase units within a translated segment. Each unit is
   * one `TranslationPhrase`'s resolved text + the `[min ts, max end_ts]`
   * of its source words. Used by the canvas to animate phrase-by-phrase
   * inside a multi-phrase caption (mirrors per-word animation on the
   * English path). High-fidelity 1:1 phrase mappings collapse to one
   * unit per word — i.e. word-by-word highlighting still works.
   */
  translatedPhrases?: {
    text: string;
    ts: number;
    endTs: number;
    originalIndices?: number[];
  }[];
  /**
   * PLAT-9623: source-phrase identity for single-phrase translated
   * segments only. Used by synthetic-break commands to locate the clip
   * for a phrase. Multi-phrase segments leave this undefined; synthetic
   * breaks on those are deferred to a follow-up.
   */
  phraseOriginalIndices?: number[];
};

/**
 * Segment transcript tokens into KaraokeSegments
 * Based on KaraokeProducer.assembleKaraokeClips and KaraokeSegmenter logic
 *
 * Note: activeTranscriptTokens have removed/cut tokens already filtered out,
 * but muted tokens are included. We keep muted tokens in segments but track them
 * so they don't appear in karaoke display.
 */
export type SegmentOptions = {
  maxSegmentCharLength?: number;
  /** A "Punctuation" line-break style: no width limit, rows end at punctuation. */
  punctuationLineBreak?: PunctuationLineBreak;
};

export function segmentTokens(
  tokens: TranscriptToken[],
  options?: SegmentOptions,
): KaraokeSegment[] {
  // Punctuation style has no width limit: a row runs to its punctuation (or a
  // pause), and the renderer wraps a long row onto a second line in the box.
  const maxCharLength = options?.punctuationLineBreak
    ? Infinity
    : (options?.maxSegmentCharLength ?? MAX_SEGMENT_CHAR_LENGTH);
  const segments: KaraokeSegment[] = [];
  let currentClipTokens: TranscriptToken[] = [];
  let currentClipStartTime: number | null = null;
  let currentClipEndTime: number | null = null;
  // Rendered width of `currentClipTokens` (spaces the renderer will insert
  // included), kept incrementally so the fit test stays O(1) per token.
  let currentClipWidth = 0;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    // Close the row BEFORE the word that would overflow it, so the cap is a
    // real maximum. Add-then-check let every row run one whole word past the
    // cap, and the row is what the renderer is asked to show at once
    // (`transcript_maximum_length`) — an over-cap row came back split on the
    // canvas while the transcript panel still showed it as one row.
    // A word longer than the cap on its own still gets its own (overlong) row;
    // a row is only closed once it holds a word to close it on.
    if (
      token.type === 'text' &&
      currentClipWidth + tokenWidthInRow(tokens, i, currentClipTokens) >
        maxCharLength &&
      hasTextToken(currentClipTokens) &&
      !breakBeforeSplitsDomain(tokens, i, currentClipTokens)
    ) {
      segments.push({
        tokens: currentClipTokens,
        startTime: currentClipStartTime,
        endTime: currentClipEndTime,
      });
      currentClipTokens = [];
      currentClipStartTime = null;
      currentClipEndTime = null;
      currentClipWidth = 0;
    }

    // Re-measured deliberately: if the row was just closed above, this token
    // now starts a fresh row and carries no leading space.
    currentClipWidth += tokenWidthInRow(tokens, i, currentClipTokens);

    // Initialize clip times with first text token (including muted)
    if (token.type === 'text' && currentClipStartTime === null) {
      currentClipStartTime = token.ts;
      currentClipEndTime = token.end_ts;
    }

    // Add token to current clip (including muted tokens)
    currentClipTokens.push(token);

    // Update end time if this is a text token
    if (token.type === 'text' && token.end_ts !== null) {
      currentClipEndTime = token.end_ts;
    }

    // Check if we should end the current segment
    if (
      shouldEndSegment(
        tokens,
        i,
        options?.punctuationLineBreak === 'withCommas',
      )
    ) {
      // Append any trailing punctuation to the current clip
      // This matches KaraokeSegmenter behavior
      let nextToken = tokens[i + 1];
      while (nextToken && nextToken.type === 'punct') {
        currentClipTokens.push(nextToken);
        i++; // Advance index
        nextToken = tokens[i + 1];
      }

      segments.push({
        tokens: currentClipTokens,
        startTime: currentClipStartTime,
        endTime: currentClipEndTime,
      });

      // Reset for next clip
      currentClipTokens = [];
      currentClipStartTime = null;
      currentClipEndTime = null;
      currentClipWidth = 0;
    }
  }

  // Add remaining tokens as final clip
  if (currentClipTokens.length > 0) {
    segments.push({
      tokens: currentClipTokens,
      startTime: currentClipStartTime,
      endTime: currentClipEndTime,
    });
  }

  // Post-process: squash orphaned segments (segments with 2 or fewer words)
  return segments;
}

/**
 * Rendered width `tokens[index]` adds to the row it is appended to: the token
 * itself plus the space the renderer will insert before it. The row is a
 * contiguous slice of `tokens`, so the spacing rule can be asked about the
 * global index — it only looks at the neighbour, which is the row's last token
 * whenever the row is non-empty.
 */
function tokenWidthInRow(
  tokens: TranscriptToken[],
  index: number,
  rowTokens: TranscriptToken[],
): number {
  const spaceWidth =
    rowTokens.length > 0 && needsSpaceBefore(tokens, index) ? 1 : 0;
  return spaceWidth + visualTextWidth(tokens[index].value ?? '');
}

function hasTextToken(tokens: TranscriptToken[]): boolean {
  return tokens.some(token => token.type === 'text');
}

/**
 * Visual width of a row as the renderer will draw it — the same join
 * `KaraokeClip.getKaraokeText` performs, spaces included.
 */
function renderedRowWidth(tokens: TranscriptToken[]): number {
  let width = 0;
  for (let i = 0; i < tokens.length; i++) {
    width += tokenWidthInRow(tokens, i, i === 0 ? [] : tokens);
  }
  return width;
}

/**
 * True when closing the row right before `index` would strand a website's TLD
 * on the next row ("example." | "com"). The length rule is allowed to run a few
 * characters long instead — the lesser evil (PLAT-10961).
 */
function breakBeforeSplitsDomain(
  tokens: TranscriptToken[],
  index: number,
  rowTokens: TranscriptToken[],
): boolean {
  let last: TranscriptToken | undefined;
  for (let i = rowTokens.length - 1; i >= 0; i--) {
    if ((rowTokens[i].value ?? '').trim() !== '') {
      last = rowTokens[i];
      break;
    }
  }
  return (
    last?.type === 'punct' &&
    last.value === '.' &&
    reachesWebsiteTld(tokens, index)
  );
}

/**
 * Check if token is muted (should be hidden but still included in clip)
 * Muted tokens are tracked separately
 */
function isTokenMuted(token: TranscriptToken): boolean {
  void token;
  // None muted initially
  // TODO: check for filler words if config hide fillers is enabled
  return false;
}

/**
 * Determine if we should end the current segment
 * Based on KaraokeSegmenter.shouldEndSegment logic
 *
 * Note: This is called AFTER the token at currentIndex has been added to currentTokens,
 * so if we return true, the token WITH the break mark will be included in the current clip.
 */
function shouldEndSegment(
  allTokens: TranscriptToken[],
  currentIndex: number,
  breakOnComma: boolean,
): boolean {
  const currentToken = allTokens[currentIndex];

  // The max-length rule lives in `segmentTokens` itself: it is a check-BEFORE-
  // add test (the row closes before the word that would overflow it), which
  // this after-the-fact hook cannot express.

  // Check for significant punctuation — ASCII and CJK sentence enders
  // (a Japanese-source transcript's 。 is a sentence end exactly like ".").
  if (
    currentToken.type === 'punct' &&
    currentToken.value &&
    ['.', '!', '?', '。', '！', '？'].includes(currentToken.value)
  ) {
    // Keep a website domain on one caption line: don't break at the "." in
    // "example.com" (the next token being a lowercase TLD marks it a domain).
    if (!continuesDomain(allTokens, currentIndex)) {
      return true;
    }
  }

  if (
    breakOnComma &&
    currentToken.type === 'punct' &&
    currentToken.value &&
    [',', '，', '、'].includes(currentToken.value)
  ) {
    return true;
  }

  // Check for pause or time gap
  if (currentToken.type === 'text') {
    const nextTextToken = findNextTextToken(allTokens, currentIndex);
    if (nextTextToken) {
      const currentEndTs = currentToken.end_ts || currentToken.ts;
      const gap = nextTextToken.ts! - currentEndTs!;

      if (gap > PAUSE_DURATION_MS) {
        return true;
      }
    }
  }

  return false;
}

/**
 * True when breaking the caption line right after `index` would split a website
 * domain ("www.example.com"). Covers both halves: a domain label (followed by
 * "." + more domain) and the period itself (followed by domain).
 */
function continuesDomain(tokens: TranscriptToken[], index: number): boolean {
  const token = tokens[index];
  if (!token) return false;
  if (token.type === 'text') {
    const period = tokens[index + 1];
    return (
      period?.type === 'punct' &&
      period.value === '.' &&
      reachesWebsiteTld(tokens, index + 2)
    );
  }
  return token.value === '.' && reachesWebsiteTld(tokens, index + 1);
}

/**
 * True when the boundary between two adjacent clips falls inside a website
 * domain — the previous clip ends with the domain's "." and the next starts
 * with its TLD. Whitespace is skipped because `segmentTokens` appends trailing
 * punctuation (spaces included) to the clip it just closed.
 */
function boundarySplitsDomain(
  prevTokens: TranscriptToken[],
  nextTokens: TranscriptToken[],
): boolean {
  const isBlank = (t: TranscriptToken) => (t.value ?? '').trim() === '';
  const lastPrev = [...prevTokens].reverse().find(t => !isBlank(t));
  const firstNextIndex = nextTokens.findIndex(t => !isBlank(t));
  return (
    lastPrev?.type === 'punct' &&
    lastPrev.value === '.' &&
    firstNextIndex >= 0 &&
    reachesWebsiteTld(nextTokens, firstNextIndex)
  );
}

/**
 * Find the next text token from current position
 */
function findNextTextToken(
  tokens: TranscriptToken[],
  fromIndex: number,
): TranscriptTextToken | null {
  for (let i = fromIndex + 1; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.type === 'text') {
      return token;
    }
  }
  return null;
}

/**
 * Get combined text from token array (excluding muted tokens)
 * Used for calculating line length for segmentation
 */
function getTextFromTokens(tokens: TranscriptToken[]): string {
  return tokens
    .filter(t => !isTokenMuted(t))
    .map(t => t.value || '')
    .join('');
}

/**
 * Squash orphaned segments (similar to KaraokeSegmenter.squashOrphanedSegments)
 * Merges segments with 2 or fewer visible (non-muted) words into the previous segment
 */
export function squashOrphanedSegments(
  clips: KaraokeClip[],
  maxCharLength?: number,
): KaraokeClip[] {
  // A merge that would push the joined row past a configured line length is
  // refused: the cap is a hard maximum (the row is one caption line), so a
  // sentence tail short enough to look stranded still has to stay its own row
  // when it cannot fit. Without a cap (the default line length) every merge is
  // allowed, exactly as before.
  const fits = (left: TranscriptToken[], right: TranscriptToken[]): boolean =>
    maxCharLength === undefined ||
    renderedRowWidth([...left, ...right]) <= maxCharLength;

  for (let i = 0; i < clips.length; i++) {
    const currentClip = clips[i];
    const prevClip = clips[i - 1] ?? null;

    // Count visible (non-muted) text tokens in current clip
    const visibleTextTokenCount = currentClip.transcriptTokens.filter(
      t => t.type === 'text' && !isTokenMuted(t),
    ).length;

    if (visibleTextTokenCount <= ORPHANED_WORDS_COUNT) {
      const mergedBackward =
        prevClip !== null &&
        fits(prevClip.transcriptTokens, currentClip.transcriptTokens) &&
        shouldMergeClips(
          prevClip.transcriptTokens,
          currentClip.transcriptTokens,
        );

      if (mergedBackward) {
        prevClip!.transcriptTokens.push(...currentClip.transcriptTokens);
        prevClip!.hiddenTranscriptTokenIds.push(
          ...currentClip.hiddenTranscriptTokenIds,
        );
        clips.splice(i, 1);
        i--; // re-evaluate the now-extended prev clip
      } else {
        const nextClip = clips[i + 1];
        if (
          nextClip &&
          fits(currentClip.transcriptTokens, nextClip.transcriptTokens) &&
          shouldMergeForward(
            currentClip.transcriptTokens,
            nextClip.transcriptTokens,
          )
        ) {
          nextClip.transcriptTokens.unshift(...currentClip.transcriptTokens);
          nextClip.hiddenTranscriptTokenIds.unshift(
            ...currentClip.hiddenTranscriptTokenIds,
          );
          clips.splice(i, 1);
          // Don't decrement — clips[i] is now the merged next clip; let the
          // loop advance naturally so we don't re-evaluate it as an orphan.
        }
      }
    }
  }

  return maxCharLength === undefined
    ? clips
    : rebalanceStrandedWords(clips, maxCharLength);
}

/**
 * Rescue a row left holding a single word. Under a configured line length a
 * short sentence tail cannot merge backward — the previous row is full, which
 * is exactly why the tail was stranded — so pull the previous row's last word
 * DOWN into it instead ("pick up and look at" / "culture." becomes "pick up and
 * look" / "at culture."). Both rows keep fitting, and no row is left hanging on
 * one word.
 *
 * Only within a sentence: when the previous row ends one, the lone word starts
 * a new sentence and belongs on its own row.
 */
function rebalanceStrandedWords(
  clips: KaraokeClip[],
  maxCharLength: number,
): KaraokeClip[] {
  for (let i = 1; i < clips.length; i++) {
    const row = clips[i];
    const prev = clips[i - 1];

    if (visibleWordCount(row.transcriptTokens) !== 1) continue;
    if (visibleWordCount(prev.transcriptTokens) < 3) continue;
    if (
      endsSentenceIgnoringAbbreviations(
        getTextFromTokens(prev.transcriptTokens),
      )
    ) {
      continue;
    }

    // The previous row's last word, with any punctuation trailing it.
    const lastWordIndex = findLastTextTokenIndex(prev.transcriptTokens);
    if (lastWordIndex < 0) continue;
    const moved = prev.transcriptTokens.slice(lastWordIndex);

    // Never move a word across a silence the segmenter broke on: the word is
    // spoken before the pause, so the receiving row would render from that
    // word and sit on screen through the gap. Same guard the orphan merges
    // carry (`shouldMergeClips` / `shouldMergeForward`) — at the widened beat
    // tolerance, since the lone word continues the previous row's sentence
    // (its not ending one is checked above).
    const movedWord = moved[0];
    const movedEnd =
      movedWord.type === 'text' ? (movedWord.end_ts ?? movedWord.ts) : null;
    const firstWordOfRow = row.transcriptTokens.find(
      (token): token is TranscriptTextToken => token.type === 'text',
    );
    const gapToRow =
      movedEnd != null && firstWordOfRow?.ts != null
        ? firstWordOfRow.ts - movedEnd
        : null;

    if (
      renderedRowWidth([...moved, ...row.transcriptTokens]) > maxCharLength ||
      (gapToRow !== null && gapToRow > NATURAL_BEAT_DURATION_MS) ||
      breakBeforeSplitsDomain(
        prev.transcriptTokens,
        lastWordIndex,
        prev.transcriptTokens.slice(0, lastWordIndex),
      )
    ) {
      continue;
    }

    prev.transcriptTokens.splice(lastWordIndex);
    row.transcriptTokens.unshift(...moved);

    const movedIds = new Set(moved.map(token => token.id));
    const stillHidden = prev.hiddenTranscriptTokenIds.filter(
      id => !movedIds.has(id),
    );
    const nowHidden = prev.hiddenTranscriptTokenIds.filter(id =>
      movedIds.has(id),
    );
    prev.hiddenTranscriptTokenIds = stillHidden;
    row.hiddenTranscriptTokenIds.unshift(...nowHidden);
  }

  return clips;
}

function visibleWordCount(tokens: TranscriptToken[]): number {
  return tokens.filter(t => t.type === 'text' && !isTokenMuted(t)).length;
}

function findLastTextTokenIndex(tokens: TranscriptToken[]): number {
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (tokens[i].type === 'text') return i;
  }
  return -1;
}

/**
 * Check if an orphan that couldn't merge backward should merge forward into the
 * next clip. The orphan starts a new sentence; merge it forward unless the gap
 * to the next clip is genuinely long.
 */
function shouldMergeForward(
  orphanTokens: TranscriptToken[],
  nextTokens: TranscriptToken[],
): boolean {
  // A sentence-ending orphan is a valid caption boundary — don't push it forward.
  if (endsSentenceIgnoringAbbreviations(getTextFromTokens(orphanTokens))) {
    return false;
  }
  const lastOrphan = orphanTokens[orphanTokens.length - 1];
  const firstNext = nextTokens[0];
  if (lastOrphan?.type === 'text' && firstNext?.type === 'text') {
    const gap = firstNext.ts! - (lastOrphan.end_ts || lastOrphan.ts)!;
    const gapLimit = endsWithAbbreviation(getTextFromTokens(orphanTokens))
      ? NATURAL_BEAT_DURATION_MS
      : PAUSE_DURATION_MS;
    if (gap > gapLimit) {
      return false;
    }
  }
  return true;
}

/**
 * Check if two segments should be merged
 */
function shouldMergeClips(
  prevTokens: TranscriptToken[],
  currentTokens: TranscriptToken[],
): boolean {
  // Don't merge when the previous line already ended a sentence — the orphan
  // starts a new one. An acronym's trailing period ("...U.S.") is NOT treated
  // as a sentence end, so its following word ("soccer.") can still rejoin it.
  // Neither is a domain's period ("...example." | "com"): `shouldEndSegment`
  // won't break there, but a custom `transcript_maximum_length` can still land
  // mid-domain, so the orphan pass has to be able to repair it (PLAT-10961).
  if (
    endsSentenceIgnoringAbbreviations(getTextFromTokens(prevTokens)) &&
    !boundarySplitsDomain(prevTokens, currentTokens)
  ) {
    return false;
  }

  // Sentence-completion exception: the orphan finishes the same sentence the
  // previous line started (prev doesn't end a sentence, checked above). Rejoin
  // it across a short pause so the sentence's final word isn't stranded, but
  // still leave a genuinely long pause as its own caption.
  //
  // Also applies when the orphan itself ends with an abbreviation ("The U.S.")
  // — the sentence clearly continues, so treat it the same as a sentence-
  // completing tail for the purpose of widening the gap tolerance.
  const currentText = getTextFromTokens(currentTokens);
  const completesSameSentence =
    endsSentenceIgnoringAbbreviations(currentText) ||
    endsWithAbbreviation(currentText);

  // Don't merge across time gaps.
  const lastTokenOfPrev = prevTokens[prevTokens.length - 1];
  const firstTokenOfCurrent = currentTokens[0];
  if (
    lastTokenOfPrev?.type === 'text' &&
    firstTokenOfCurrent?.type === 'text'
  ) {
    const gap =
      firstTokenOfCurrent.ts! - (lastTokenOfPrev.end_ts || lastTokenOfPrev.ts)!;
    const gapLimit = completesSameSentence
      ? NATURAL_BEAT_DURATION_MS
      : PAUSE_DURATION_MS;
    if (gap > gapLimit) {
      return false;
    }
  }

  return true;
}

/**
 * Concatenate two translated fragments with the punctuation-aware spacing the
 * prose views use, so folding a `'.'` onto a phrase yields `'… Dingo.'` and not
 * `'… Dingo .'`, while an opening `'«'` keeps its space. Either side may be
 * empty.
 *
 * When no space belongs between them, `left`'s OWN trailing whitespace is
 * dropped too: phrase values carry their spacing inconsistently (some end in a
 * space, e.g. `'потому что вам нужен погрузчик, '`), and concatenating a period
 * straight onto one of those would render `'Почувствуйте больше .'`.
 */
function appendPunctuation(left: string, right: string): string {
  if (!left) return right;
  if (!right) return left;
  return needsSpaceBetweenPhrases(left, right)
    ? `${left} ${right}`
    : `${left.replace(/\s+$/, '')}${right}`;
}

/**
 * PLAT-9621 — Phrase-boundary segmentation for translated captions.
 *
 * Each `TranslationPhrase` becomes one segment; segment timing comes from
 * `[min(ts), max(end_ts)]` of the source tokens at `phrase.originalIndices`.
 * This is the architectural alternative to character-length segmentation
 * (`segmentTokens`) and avoids FM-1 (no synthetic per-word timestamps on
 * translated text).
 *
 * Empty / fully-removed phrases are skipped (no caption line emitted).
 *
 * Punctuation is FOLDED into the preceding phrase's text rather than dropped.
 * The splitter routinely emits punct-only phrases — one `originalIndices` entry
 * pointing at a punct element, with the punctuation as the whole
 * `translatedValue` (`{ originalIndices: [28], translatedValue: '.' }`). Those
 * have no text token, so they get no bucket below and `resolvePhraseTiming`
 * returns `null` for them: they can never be a caption row of their own. They
 * used to be skipped outright, which silently deleted EVERY sentence-final `.`
 * from translated captions while commas that happened to sit INSIDE a
 * word-bearing phrase's value survived — the reported "captions have commas but
 * no periods". Unphrased punct tokens fold the same way, using their raw source
 * value.
 *
 * Whitespace-only punct is still dropped — the space join below already supplies
 * inter-phrase spacing, so emitting it would double up.
 *
 * Consequence: a punct-only phrase gets no `translatedPhrases` entry of its own,
 * so `SyncTranslatedCaptionTextCommand` (which matches entries by
 * `originalIndices[0]`) cannot re-text one in isolation. Accepted — a lone
 * punctuation phrase is not an editable unit in the UI.
 *
 * Within-phrase synthetic line breaks (T49) layer on top of this
 * segmentation in a follow-up ticket — for T47, every phrase = one line
 * regardless of length.
 */
export function segmentTokensByPhrase(
  tokens: TranscriptToken[],
  phrases: readonly TranslationPhrase[],
  resolveTranslatedValue: (originalIndices: number[]) => string | null,
  options?: SegmentOptions,
): KaraokeSegment[] {
  const maxCharLength =
    options?.maxSegmentCharLength ?? MAX_SEGMENT_CHAR_LENGTH;
  const resolved = resolvePhraseCaptionRows(
    tokens,
    phrases,
    resolveTranslatedValue,
  );
  return mergeResolvedPhrasesIntoSegments(
    resolved,
    maxCharLength,
    options?.punctuationLineBreak,
  );
}

/**
 * One caption-row unit: a phrase, the tokens carrying its timing, and the text
 * that belongs on screen for it — punctuation already folded in.
 */
export interface ResolvedPhraseRow {
  phrase: TranslationPhrase;
  bucket: TranscriptToken[];
  ts: number;
  endTs: number;
  text: string;
  originalIndices: number[];
}

/**
 * THE single source of truth for "what text belongs on a caption row for this
 * phrase". Everything that needs to know a phrase's caption text must come
 * through here — the producer (`segmentTokensByPhrase`, which then merges these
 * rows into segments) and the in-place text sync
 * (`computeTranslatedCaptionTextPatches`, which compares against them).
 *
 * Why it is shared rather than re-derived: the text is NOT
 * `phrase.translatedValue`. Punctuation with no row of its own is folded in
 * here, so a row reads `'… Dingo.'` while the phrase's own value is still
 * `'… Dingo'`. A second implementation that missed that folding reported every
 * folded row permanently stale AND wrote the unfolded value back, deleting the
 * punctuation the producer had just restored. Keep one implementation and that
 * class of bug cannot recur.
 */
/**
 * `[min(ts), max(end_ts)]` over a phrase's own text tokens, skipping the null
 * timestamps a cut token can carry. Null when nothing timeable is left. Unlike
 * `resolvePhraseTiming` this trusts the bucket instead of re-matching by
 * `initial_index`, so it survives clip reuse.
 */
export function phraseTimingFromBucket(
  bucket: readonly TranscriptToken[],
): { ts: number; endTs: number } | null {
  let minTs = Infinity;
  let maxEnd = -Infinity;
  for (const token of bucket) {
    if (token.type !== 'text') continue;
    const ts = token.ts;
    const end = token.end_ts;
    if (ts == null || end == null) continue;
    if (ts < minTs) minTs = ts;
    if (end > maxEnd) maxEnd = end;
  }
  if (minTs === Infinity || maxEnd === -Infinity) return null;
  return { ts: minTs, endTs: maxEnd };
}

export function resolvePhraseCaptionRows(
  tokens: TranscriptToken[],
  phrases: readonly TranslationPhrase[],
  resolveTranslatedValue: (originalIndices: number[]) => string | null,
  resolvePhraseForToken?: (token: TranscriptToken) => TranslationPhrase | null,
): ResolvedPhraseRow[] {
  // Group tokens by phrase. The DEFAULT join is positional
  // (`initial_index → phrase`); callers that must survive clip reuse pass a
  // UUID-first `resolvePhraseForToken` instead (see `makePhraseForTokenResolver`)
  // — a reused clip renumbers `initial_index` to `-1`, so the index join alone
  // would resolve every phrase as absent. Phrases that don't appear in any
  // active token are skipped — they're fully removed/cut.
  const phraseByIndex = new Map<number, TranslationPhrase>();
  for (const phrase of phrases) {
    for (const idx of phrase.originalIndices) {
      phraseByIndex.set(idx, phrase);
    }
  }
  const resolvePhrase =
    resolvePhraseForToken ??
    ((token: TranscriptToken) =>
      phraseByIndex.get(token.initial_index) ?? null);

  // Collect tokens per phrase, preserving the order phrases appear in
  // the source transcript (smallest originalIndex first).
  const tokensByPhrase = new Map<TranslationPhrase, TranscriptToken[]>();
  const seenPhrases: TranslationPhrase[] = [];
  for (const token of tokens) {
    if (token.type !== 'text') continue;
    const phrase = resolvePhrase(token);
    if (!phrase) continue;
    let bucket = tokensByPhrase.get(phrase);
    if (!bucket) {
      bucket = [];
      tokensByPhrase.set(phrase, bucket);
      seenPhrases.push(phrase);
    }
    bucket.push(token);
  }

  // Second pass — collect the punctuation that pass 1 skipped and attach it to
  // the phrase it follows, BY PHRASE IDENTITY (not index) so the sort below
  // can't desynchronise the attachment.
  //
  // Deliberately a separate pass: a phrase's punct token can precede its text
  // tokens (`{ originalIndices: [94, 96, …], translatedValue: ', который …' }`
  // leads on the comma), so whether a punct token's phrase has a bucket is only
  // knowable once pass 1 finished. A punct token whose phrase HAS a bucket is
  // already represented inside that phrase's `translatedValue` and must not be
  // folded again.
  const foldedAfterPhrase = new Map<TranslationPhrase, string>();
  const foldedPhrases = new Set<TranslationPhrase>();
  let leadingPunctuation = '';
  let lastBucketedPhrase: TranslationPhrase | null = null;
  for (const token of tokens) {
    const phrase = resolvePhrase(token);
    if (phrase && tokensByPhrase.has(phrase)) {
      lastBucketedPhrase = phrase;
      continue;
    }
    // Whitespace carries no meaning here — the space join supplies it.
    if (token.value == null || token.value.trim() === '') continue;
    if (token.type !== 'punct') continue;

    let value: string;
    if (phrase) {
      // A punct-only phrase covering several punct tokens resolves to ONE
      // translated value; fold it once rather than per token.
      if (foldedPhrases.has(phrase)) continue;
      foldedPhrases.add(phrase);
      value = resolveTranslatedValue(phrase.originalIndices) ?? token.value;
    } else {
      value = token.value;
    }

    if (!lastBucketedPhrase) {
      leadingPunctuation = appendPunctuation(leadingPunctuation, value);
      continue;
    }
    foldedAfterPhrase.set(
      lastBucketedPhrase,
      appendPunctuation(foldedAfterPhrase.get(lastBucketedPhrase) ?? '', value),
    );
  }

  // Sort phrases by their first originalIndex so caption-line order matches
  // transcript order even if `phrases` arrived in some other order.
  seenPhrases.sort((a, b) => {
    const minA =
      a.originalIndices.length === 0
        ? Infinity
        : Math.min(...a.originalIndices);
    const minB =
      b.originalIndices.length === 0
        ? Infinity
        : Math.min(...b.originalIndices);
    return minA - minB;
  });

  const resolved: ResolvedPhraseRow[] = [];
  for (const phrase of seenPhrases) {
    const bucket = tokensByPhrase.get(phrase);
    if (!bucket || bucket.length === 0) continue;

    // The bucket already IS this phrase's text tokens (grouped by `resolvePhrase`
    // above), so time it directly rather than re-matching by `initial_index` —
    // that index is `-1` on a reused clip, which would drop the row even though
    // the UUID join found it. `[min(ts), max(end_ts)]`, skipping the null
    // timestamps a cut token can carry.
    const timing = phraseTimingFromBucket(bucket);
    if (!timing) continue;

    const translated = resolveTranslatedValue(phrase.originalIndices);
    if (translated == null) {
      logger.warn(
        'resolvePhraseCaptionRows: translation lookup returned null, falling back to source words',
        { initialIndices: phrase.originalIndices },
      );
    }
    const base =
      translated ??
      bucket
        .map(t => t.value ?? '')
        .filter(v => v.length > 0)
        .join(' ');
    // Punctuation that has no row of its own rides on this phrase's text, so
    // the space join in the merge step and every character offset derived from
    // it stay in one frame.
    let text = appendPunctuation(base, foldedAfterPhrase.get(phrase) ?? '');
    if (resolved.length === 0 && leadingPunctuation) {
      text = appendPunctuation(leadingPunctuation, text);
      leadingPunctuation = '';
    }
    resolved.push({
      phrase,
      bucket,
      ts: timing.ts,
      endTs: timing.endTs,
      text,
      originalIndices: phrase.originalIndices,
    });
  }

  return resolved;
}

/**
 * Build the `resolveTranslatedValue` callback `resolvePhraseCaptionRows` expects,
 * indexed once so lookups are O(1) instead of the O(phrases) scan a `find` does
 * per phrase.
 *
 * FIRST match wins, matching `Array.prototype.find`: a retired fragment and the
 * active override that replaced it can carry the same `originalIndices`, and the
 * producer resolves such a pair to whichever comes first in `phrases`. Keying a
 * map naively would silently hand back the last one instead.
 */
export function makeTranslatedValueResolver(
  phrases: readonly TranslationPhrase[],
): (originalIndices: number[]) => string | null {
  const byKey = new Map<string, string>();
  for (const phrase of phrases) {
    const key = phrase.originalIndices.join(',');
    if (!byKey.has(key)) byKey.set(key, phrase.translatedValue);
  }
  return originalIndices => byKey.get(originalIndices.join(',')) ?? null;
}

/**
 * Resolve a live transcript token to its covering phrase, UUID-FIRST.
 *
 * The join matches `token.source_uuid ?? token.id` against a phrase's
 * `originalElementIds`, falling back to `initial_index → originalIndices`. This
 * is the same id-first rule the render/caption UI already uses
 * (`buildPhraseMembershipMaps`, PLAT-10611): a reused or reordered clip carries
 * the SAME `source_uuid` but a FRESH `id` and an `initial_index` renumbered to
 * `-1`, so only the UUID path can rejoin it — the index path alone would resolve
 * every phrase as absent and drop the whole translation.
 *
 * Retired fragments (`active === false`) are excluded so they never shadow the
 * live override that replaced them. FIRST registration wins for a given key,
 * matching the render membership map.
 */
export function makePhraseForTokenResolver(
  phrases: readonly TranslationPhrase[],
): (token: TranscriptToken) => TranslationPhrase | null {
  const byUuid = new Map<string, TranslationPhrase>();
  const byIndex = new Map<number, TranslationPhrase>();
  for (const phrase of phrases) {
    if (phrase.active === false) continue;
    if (phrase.originalElementIds) {
      for (const id of phrase.originalElementIds) {
        if (!byUuid.has(id)) byUuid.set(id, phrase);
      }
    }
    for (const idx of phrase.originalIndices) {
      if (!byIndex.has(idx)) byIndex.set(idx, phrase);
    }
  }
  return token =>
    byUuid.get(token.source_uuid ?? token.id) ??
    byIndex.get(token.initial_index) ??
    null;
}

/**
 * Producer-authored caption text keyed by each phrase's LEAD source index
 * (`originalIndices[0]`) — the key the sync groups caption entries by, since the
 * producer stamps `originalIndices: phrase.originalIndices` on every entry it
 * emits. Thin projection of {@link resolvePhraseCaptionRows}.
 */
export function resolvePhraseCaptionTexts(
  tokens: TranscriptToken[],
  phrases: readonly TranslationPhrase[],
  resolveTranslatedValue: (originalIndices: number[]) => string | null,
): Map<number, string> {
  const byLeadIndex = new Map<number, string>();
  for (const row of resolvePhraseCaptionRows(
    tokens,
    phrases,
    resolveTranslatedValue,
  )) {
    const lead = row.originalIndices[0];
    if (lead !== undefined) byLeadIndex.set(lead, row.text);
  }
  return byLeadIndex;
}

/**
 * Combine consecutive resolved phrases into one caption segment until the
 * running translated-text length passes `maxCharLength`, OR the running text
 * ends with sentence-final punctuation. Mirrors the English-side
 * `segmentTokens` line-length heuristic so translated captions don't render one
 * row per (often single-word) phrase.
 *
 * Each emitted segment carries: combined source tokens (timing carrier),
 * `[min(ts), max(end_ts)]` over those tokens, and space-joined translatedText.
 *
 * Text is NOT touched here — it is final by the time rows arrive. Anything that
 * transformed text at this stage would be invisible to the sync, which reads
 * `resolvePhraseCaptionRows` directly; keep row text decisions upstream.
 */
export function mergeResolvedPhrasesIntoSegments(
  // PLAT-11905: the merger never reads a row's `phrase`, so a caller with no
  // translation phrase behind its units (the dub-timed rows, built from a dub
  // line's words) can use the same row rules.
  resolved: readonly Omit<ResolvedPhraseRow, 'phrase'>[],
  configuredMaxCharLength: number,
  punctuationLineBreak?: PunctuationLineBreak,
): KaraokeSegment[] {
  // Punctuation styles have no width limit in any script: a row runs to its
  // punctuation, and the canvas wraps it onto a second line.
  const maxCharLength = punctuationLineBreak
    ? Infinity
    : configuredMaxCharLength;
  // ASCII + CJK sentence enders (PLAT-11468) — Japanese lines must end at
  // 。 the way Latin lines end at ".". CJK closing quotes/brackets may trail
  // the ender (「…。」); Latin trailing quotes deliberately stay non-final
  // (pre-existing behavior, unchanged).
  const endsSentence = (s: string): boolean =>
    /[.!?。！？][」』〉]*\s*$/.test(s);
  const endsClause = (s: string): boolean => /[,、，][」』〉]*\s*$/.test(s);
  const endsRow = (s: string): boolean =>
    endsSentence(s) || (punctuationLineBreak === 'withCommas' && endsClause(s));

  const segments: KaraokeSegment[] = [];
  let acc: Omit<ResolvedPhraseRow, 'phrase'>[] = [];
  let accLen = 0;
  const flush = () => {
    if (acc.length === 0) return;
    const tokens: TranscriptToken[] = [];
    let minTs = Infinity;
    let maxEnd = -Infinity;
    for (const r of acc) {
      tokens.push(...r.bucket);
      if (r.ts < minTs) minTs = r.ts;
      if (r.endTs > maxEnd) maxEnd = r.endTs;
    }
    segments.push({
      tokens,
      startTime: minTs,
      endTime: maxEnd,
      // CJK-aware join (PLAT-11468): no ASCII space between full-width rows.
      translatedText: joinCaptionRowTexts(acc.map(r => r.text)),
      translatedPhrases: acc.map(r => ({
        text: r.text,
        ts: r.ts,
        endTs: r.endTs,
        originalIndices: [...r.originalIndices],
      })),
      // Single-phrase segment only — synthetic breaks (PLAT-9623) on
      // multi-phrase segments are deferred. Single phrase = the only
      // case the over-length-phrase use case actually matters in.
      phraseOriginalIndices:
        acc.length === 1 ? [...acc[0].originalIndices] : undefined,
    });
    acc = [];
    accLen = 0;
  };

  // PLAT-11481 — an over-length FULL-WIDTH row cannot be rescued by the
  // renderer (spaceless text has no wrap opportunities) and cannot be
  // shortened by merging: split it INSIDE the phrase at word boundaries,
  // greedily packing words up to the cap by visual width. Times come from
  // the same machinery as manual line breaks (`applySyntheticBreaksToPhrase`
  // with fabricated positions), and the emitted sub-segments carry the
  // synthetic-break clip shape: shared token bucket + interior offsets +
  // `phraseOriginalIndices`, so sync/redistribute and manual breaks compose.
  // Latin over-length rows keep today's behavior (renderer wraps at spaces).
  const splitOverLengthWideRow = (
    r: Omit<ResolvedPhraseRow, 'phrase'>,
  ): PhraseUnit[] | null => {
    if (!containsWideChar(r.text)) return null;
    if (visualTextWidth(r.text) <= maxCharLength) return null;
    const spans = computeWordSpans(r.text);
    if (spans.length < 2) return null;
    const positions: Array<{ textPosition: number }> = [];
    let lineStart = 0;
    for (let i = 1; i < spans.length; i++) {
      // Width of the line if it ran through word i — the slice keeps the
      // original separators, so Korean spaces count too.
      if (
        visualTextWidth(r.text.slice(lineStart, spans[i].end)) > maxCharLength
      ) {
        positions.push({ textPosition: spans[i].start });
        lineStart = spans[i].start;
      }
    }
    if (positions.length === 0) return null;
    const units = applySyntheticBreaksToPhrase(
      { text: r.text, ts: r.ts, endTs: r.endTs },
      positions,
    );
    return units.length > 1 ? units : null;
  };

  // Under Punctuation (with commas), a comma INSIDE a phrase is a row break
  // too — a translation often places 、 where the source had no phrase
  // boundary, so `endsRow` (which only sees the phrase's end) misses it.
  // Split there with the same machinery as the over-length split.
  // PLAT-11905: reads only the row's text and times, so dub-timed rows
  // (which carry no `phrase`) get the same comma rule.
  const splitAtInteriorCommas = (
    r: Omit<ResolvedPhraseRow, 'phrase'>,
  ): PhraseUnit[] | null => {
    if (punctuationLineBreak !== 'withCommas') return null;
    const positions: Array<{ textPosition: number }> = [];
    // A comma (plus any closing quotes/brackets, so they don't open the next
    // row) followed by more text. A Latin comma must be followed by a space:
    // "1,000" / "3,5" are numbers, and the text sync can't rebuild a split
    // "a,b" (it rejoins as "a, b"). Its closers may be space-separated
    // (French "oui, »"). "，" is skipped only between digits ("１，０００").
    const interiorComma =
      /,(?:\s*[」』〉”’»)\]])*(?=\s|$)|(?:、|(?<!\p{Nd})，|，(?!\p{Nd}))[」』〉]*/gu;
    for (const match of r.text.matchAll(interiorComma)) {
      const end = (match.index ?? 0) + match[0].length;
      if (r.text.slice(end).trim().length > 0) {
        positions.push({ textPosition: end });
      }
    }
    if (positions.length === 0) return null;
    const units = applySyntheticBreaksToPhrase(
      { text: r.text, ts: r.ts, endTs: r.endTs },
      positions,
    );
    return units.length > 1 ? units : null;
  };

  for (const r of resolved) {
    const autoUnits = splitOverLengthWideRow(r) ?? splitAtInteriorCommas(r);
    if (autoUnits) {
      flush();
      // Deterministic per-phrase id: the reconcile retime-skip and the
      // merge command's shared-reference check both depend on sub-rows
      // being recognizably one family.
      const siblingGroupId = `autosplit:${r.originalIndices.join('-')}`;
      for (let i = 0; i < autoUnits.length; i++) {
        const unit = autoUnits[i];
        segments.push({
          // The SAME array reference on every sub-segment — reference
          // equality is the merge command's previously-split signal.
          tokens: r.bucket,
          startTime: unit.ts,
          endTime: unit.endTs,
          translatedText: unit.text,
          translatedPhrases: [
            {
              text: unit.text,
              ts: unit.ts,
              endTs: unit.endTs,
              originalIndices: [...r.originalIndices],
            },
          ],
          phraseOriginalIndices: [...r.originalIndices],
          siblingGroupId,
          ...(i > 0 ? { startSecondsOffset: unit.ts - r.ts } : {}),
          ...(i < autoUnits.length - 1
            ? { endSecondsOffset: unit.endTs - r.endTs }
            : {}),
        });
      }
      continue;
    }
    // Visual width (PLAT-11468): full-width characters count 2, and the
    // joiner is only counted where the join will actually insert one.
    const width = visualTextWidth(r.text);
    const joinerWith = (prev: string | null) =>
      prev !== null && needsSpaceBetweenCaptionRows(prev, r.text) ? 1 : 0;
    // Flush BEFORE adding a FULL-WIDTH row that won't fit — greedy
    // add-then-check let a pair of medium rows land at ~2× the cap, which
    // the renderer cannot wrap for spaceless text. Latin rows keep the
    // greedy behavior byte-identical: their overflow wraps at spaces, and
    // strict fit would strand short rows alone. A row longer than the cap
    // on its own still gets its own (overlong) segment, as before.
    const prevText = acc.length > 0 ? acc[acc.length - 1].text : null;
    if (
      accLen > 0 &&
      containsWideChar(r.text) &&
      accLen + joinerWith(prevText) + width > maxCharLength
    ) {
      flush();
    }
    const prev = acc.length > 0 ? acc[acc.length - 1].text : null;
    acc.push(r);
    accLen += width + joinerWith(prev);
    if (accLen >= maxCharLength || endsRow(r.text)) {
      flush();
    }
  }
  flush();

  return segments;
}

/**
 * Join caption-row texts: `joinPhraseValues` under the karaoke producer's name
 * (no space inside CJK prose — PLAT-11468, PLAT-11636). THE join for
 * caption-row text everywhere: the producer, refresh/reconcile re-joins, and
 * the split/merge commands all must agree, or an edit reintroduces the stray
 * mid-sentence space.
 */
export function joinCaptionRowTexts(texts: readonly string[]): string {
  return joinPhraseValues(texts);
}

/**
 * Resolves overlaps between karaoke clips by adjusting offsets.
 * Strategy:
 * 1. For each overlapping pair, prefer adjusting clips with expanded offsets
 * 2. Reduce expanded offsets toward zero (shrinking clip back to natural duration)
 * 3. Only shrink clips beyond natural duration if reducing expanded offsets doesn't resolve overlap
 *
 * Note:
 * - Negative startSecondsOffset means clip starts earlier (expanded backward)
 * - Positive endSecondsOffset means clip ends later (expanded forward)
 */
export function resolveOverlaps(clips: KaraokeClip[]): void {
  for (let i = 0; i < clips.length - 1; i++) {
    const currentClip = clips[i];
    const nextClip = clips[i + 1];

    const currentEnd = currentClip.getKaraokeEndTime();
    const nextStart = nextClip.getKaraokeStartTime();

    if (currentEnd === null || nextStart === null) {
      continue;
    }

    // Check for overlap
    if (currentEnd > nextStart) {
      const overlap = currentEnd - nextStart;
      console.warn(
        `Detected overlap of ${overlap}s between clips ${currentClip.id} and ${nextClip.id}`,
      );

      resolveOverlapBetweenClips(currentClip, nextClip, overlap);
    }
  }
}

/**
 * Resolves overlap between two adjacent clips
 */
function resolveOverlapBetweenClips(
  clip1: KaraokeClip,
  clip2: KaraokeClip,
  overlap: number,
): void {
  const clip1EndOffset = clip1.endSecondsOffset || 0;
  const clip2StartOffset = clip2.startSecondsOffset || 0;

  // Strategy 1: Try to reduce expanded offsets toward zero
  // - clip1 with positive endSecondsOffset is expanded forward
  // - clip2 with negative startSecondsOffset is expanded backward
  const clip1ExpandedForward = Math.max(0, clip1EndOffset);
  const clip2ExpandedBackward = Math.abs(Math.min(0, clip2StartOffset));

  if (clip1ExpandedForward > 0 || clip2ExpandedBackward > 0) {
    const totalCanReduce = clip1ExpandedForward + clip2ExpandedBackward;

    if (totalCanReduce >= overlap) {
      // We can fully resolve by reducing expanded portions
      const clip1Reduction = Math.min(clip1ExpandedForward, overlap);
      const remainingOverlap = overlap - clip1Reduction;
      const clip2Reduction = Math.min(clip2ExpandedBackward, remainingOverlap);

      if (clip1Reduction > 0) {
        const newEndOffset = clip1EndOffset - clip1Reduction;
        clip1.endSecondsOffset = newEndOffset;
        console.log(
          `Resolved overlap by reducing clip ${clip1.id} endSecondsOffset from ${clip1EndOffset} to ${newEndOffset}`,
        );
      }

      if (clip2Reduction > 0) {
        const newStartOffset = clip2StartOffset + clip2Reduction;
        clip2.startSecondsOffset = newStartOffset;
        console.log(
          `Resolved overlap by reducing clip ${clip2.id} startSecondsOffset from ${clip2StartOffset} to ${newStartOffset}`,
        );
      }

      if (clip1Reduction + clip2Reduction >= overlap) {
        return;
      }
    } else {
      // Partially resolved, apply all available reductions
      if (clip1ExpandedForward > 0) {
        clip1.endSecondsOffset = 0;
        console.log(
          `Partially resolved overlap by resetting clip ${clip1.id} endSecondsOffset to 0`,
        );
      }
      if (clip2ExpandedBackward > 0) {
        clip2.startSecondsOffset = 0;
        console.log(
          `Partially resolved overlap by resetting clip ${clip2.id} startSecondsOffset to 0`,
        );
      }
      overlap = overlap - totalCanReduce;
    }
  }

  // Strategy 2: Shrink clips beyond their natural duration (inward)
  const clip1MinDuration = DEFAULT_MINIMUM_TOKEN_DURATION;
  const clip2MinDuration = DEFAULT_MINIMUM_TOKEN_DURATION;

  const clip1CurrentDuration = clip1.getKaraokeDuration();
  const clip2CurrentDuration = clip2.getKaraokeDuration();

  if (clip1CurrentDuration === null || clip2CurrentDuration === null) {
    console.error('Cannot resolve overlap: invalid clip durations');
    return;
  }

  // Calculate how much we can shrink each clip
  const clip1CanShrink = Math.max(0, clip1CurrentDuration - clip1MinDuration);
  const clip2CanShrink = Math.max(0, clip2CurrentDuration - clip2MinDuration);

  if (clip1CanShrink + clip2CanShrink < overlap) {
    console.error(
      `Cannot fully resolve overlap: clips cannot shrink enough (need ${overlap}s, can shrink ${
        clip1CanShrink + clip2CanShrink
      }s)`,
    );
    // Still apply maximum possible shrinkage
  }

  // Distribute shrinkage proportionally
  let clip1Shrink = 0;
  let clip2Shrink = 0;
  const halfOverlap = overlap / 2;

  if (clip1CanShrink >= halfOverlap && clip2CanShrink >= halfOverlap) {
    // Both can accommodate half the overlap
    clip1Shrink = halfOverlap;
    clip2Shrink = halfOverlap;
  } else if (clip1CanShrink < halfOverlap) {
    // Clip1 can't shrink enough, clip2 takes more
    clip1Shrink = clip1CanShrink;
    clip2Shrink = Math.min(clip2CanShrink, overlap - clip1Shrink);
  } else {
    // Clip2 can't shrink enough, clip1 takes more
    clip2Shrink = clip2CanShrink;
    clip1Shrink = Math.min(clip1CanShrink, overlap - clip2Shrink);
  }

  if (clip1Shrink > 0) {
    const newEndOffset = (clip1.endSecondsOffset || 0) - clip1Shrink;
    clip1.endSecondsOffset = newEndOffset;
    console.log(
      `Resolved overlap by shrinking clip ${clip1.id} endSecondsOffset to ${newEndOffset} (reduced by ${clip1Shrink}s)`,
    );
  }

  if (clip2Shrink > 0) {
    const newStartOffset = (clip2.startSecondsOffset || 0) + clip2Shrink;
    clip2.startSecondsOffset = newStartOffset;
    console.log(
      `Resolved overlap by shrinking clip ${clip2.id} startSecondsOffset to ${newStartOffset} (increased by ${clip2Shrink}s)`,
    );
  }
}
